// ══════════════════════════════════════════════════════════════════════════
// AS OPÇÕES DA FILA DA DUDA — uma definição só, para as CINCO telas.
//
// 🟥 O QUE ACONTECIA (15/09/2026)
//
// `GET /duda/radar` alimenta cinco superfícies: a bolha da Duda, o badge do
// menu, o Painel, "Precisa de você" e Orçamentos. Quatro delas usavam a mesma
// chave do React Query e já se deduplicavam. A QUINTA — o `DudaWidget` — usava
// `apiFetch` cru com timer próprio de 60 s, fora do cache.
//
// Resultado: em TODA tela, a rota era chamada DUAS vezes por minuto, com dois
// relógios independentes que iam derivando um do outro.
//
// ⚠️ ISSO NUNCA FOI LENTIDÃO DE TELA. As duas chamadas eram SIMULTÂNEAS, não em
// série — ninguém esperava o dobro. Era trabalho dobrado no servidor. Vale dizer
// porque a régua muda o conserto: quem lesse "2× por tela" como latência mexeria
// no lugar errado (a latência era outra coisa, e foi consertada no #2508).
//
// ══════════════════════════════════════════════════════════════════════════
// POR QUE AS OPÇÕES MORAM AQUI, E NÃO COPIADAS EM CINCO ARQUIVOS
//
// O dedup do React Query é por CHAVE: cinco observadores de `["duda-radar", id]`
// compartilham um cache e UM timer. Isso quer dizer que o dedup vale exatamente
// enquanto as cinco chaves forem IGUAIS — e cinco cópias de uma string derivam.
// Bastaria alguém escrever `["duda_radar", id]` num arquivo pra o dedup sumir em
// silêncio, sem quebrar nada, sem teste nenhum acusar.
//
// Com a fonte única, o dedup deixa de depender de coincidência.
//
// 🪤 `enabled` NÃO ENTRA AQUI. Cada tela liga a query pelo seu próprio critério
// (pele v2, papel, empresa ativa), e é assim que tem que ser. Fixar `enabled`
// aqui faria uma tela ligar a busca de outra.
// ══════════════════════════════════════════════════════════════════════════

import { apiFetch } from "./api";
import type { RadarItem } from "./radarVista";

/** A fila, como o servidor devolve (api/lib/dudaRadar.ts → montarRadar). */
export type RespostaDoRadar = {
  total?: number;
  severidade?: string;
  urgentes?: RadarItem[];
  atencao?: RadarItem[];
  /** Só a bolha usa: é ela que comemora quando cai um pagamento novo. */
  recebidoHoje?: { count: number; total: number };
};

/**
 * O intervalo do poll.
 *
 * 🔑 ELE FICA NAS OPÇÕES COMPARTILHADAS, e não em uma tela só, por um motivo que
 * não é estético: `refetchInterval` é por CHAVE, não por observador. Com as cinco
 * telas declarando o mesmo intervalo, o React Query mantém UM timer e dispara UMA
 * requisição — não cinco. E nenhuma tela passa a depender de outra estar montada
 * pra continuar atualizando.
 */
export const INTERVALO_DO_RADAR_MS = 60_000;

/** `["duda-radar", <empresa>]` — a chave que as cinco telas compartilham. */
export function chaveDoRadar(empresaId: string | undefined): [string, string | undefined] {
  return ["duda-radar", empresaId];
}

/**
 * As opções para `useQuery`. Cada tela acrescenta o seu `enabled` (e nada mais).
 *
 * 🔴 `retry: false` É COMPORTAMENTO, NÃO ENFEITE. O padrão do React Query são 3
 * tentativas — num radar best-effort isso TRIPLICARIA a carga justo no momento em
 * que o servidor está com problema. A bolha já tratava erro como silêncio (um
 * `.catch` mudo); isto mantém a régua e a estende às outras quatro.
 */
export function opcoesDoRadar(empresaId: string | undefined, token: string | null) {
  return {
    queryKey: chaveDoRadar(empresaId),
    queryFn: () => apiFetch<RespostaDoRadar>("/duda/radar", token),
    staleTime: INTERVALO_DO_RADAR_MS,
    refetchInterval: INTERVALO_DO_RADAR_MS,
    // 🟥 15/09: cheguei a pôr `retry: 1` por causa das 65 quedas de CPU em 7 dias — e TRÊS provas da casa
    // caíram ("fila caída não é fila vazia", "radar caído faz o contador SUMIR"): a repescagem atrasa o
    // estado de ERRO, e a tela fica "carregando" em vez de dizer que não sabe. Dizer a verdade rápido vale
    // mais que uma segunda tentativa: fica `false`, e quem avisa é a tela (bolha e contador).
    retry: false as const,
  };
}

/** O que a bolha mostra, venha a fila do tenant ou da plataforma. */
export type FilaMostrada = {
  total: number;
  severidade: string;
  urgentes: RadarItem[];
  atencao: RadarItem[];
};

/** Uma linha do radar da PLATAFORMA — outro endpoint, outro formato. */
export type ItemDaPlataforma = {
  titulo: string;
  detalhe?: string;
  rota?: string;
  severidade?: "urgente" | "atencao";
};

/**
 * 🪤 O RADAR DA PLATAFORMA TEM CHAVE PRÓPRIA, E ISSO NÃO É DETALHE.
 *
 * É outro endpoint (`/operador/radar`), com outro formato e outro escopo — é do
 * DONO da plataforma, não do tenant. Dividir a chave `["duda-radar", …]` com a
 * fila do tenant faria a resposta de um sobrescrever o cache do outro, e o badge
 * do menu passaria a mostrar teto de IA no lugar de conta vencida.
 */
export function opcoesDoRadarDaPlataforma(empresaId: string | undefined, token: string | null) {
  return {
    queryKey: ["operador-radar", empresaId],
    queryFn: () => apiFetch<{ total?: number; severidade?: string; itens?: ItemDaPlataforma[] }>("/operador/radar", token),
    staleTime: INTERVALO_DO_RADAR_MS,
    refetchInterval: INTERVALO_DO_RADAR_MS,
    // 🟥 15/09: cheguei a pôr `retry: 1` por causa das 65 quedas de CPU em 7 dias — e TRÊS provas da casa
    // caíram ("fila caída não é fila vazia", "radar caído faz o contador SUMIR"): a repescagem atrasa o
    // estado de ERRO, e a tela fica "carregando" em vez de dizer que não sabe. Dizer a verdade rápido vale
    // mais que uma segunda tentativa: fica `false`, e quem avisa é a tela (bolha e contador).
    retry: false as const,
  };
}

/** A resposta do tenant virando fila. `severidade` ausente = "ocioso", nunca vazio. */
export function filaDoTenant(r: RespostaDoRadar): FilaMostrada {
  return {
    total: r.total ?? 0,
    severidade: r.severidade ?? "ocioso",
    urgentes: r.urgentes ?? [],
    atencao: r.atencao ?? [],
  };
}

/**
 * A resposta da plataforma virando a MESMA fila.
 *
 * Tudo em `urgentes`: a régua de "atenção" é do tenant (vence em N dias), e a
 * plataforma não tem vencimento — tem teto estourado ou não.
 */
export function filaDaPlataforma(r: { total?: number; severidade?: string; itens?: ItemDaPlataforma[] }): FilaMostrada {
  return {
    total: r.total ?? 0,
    severidade: r.severidade ?? "ocioso",
    urgentes: (r.itens ?? []).map((i) => ({
      titulo: i.titulo,
      detalhe: i.detalhe,
      rota: i.rota,
      severidade: i.severidade,
    })) as RadarItem[],
    atencao: [],
  };
}
